/**
 * F-145 / T-106-002 + T-106-003 — Houston HRRB registration workflow page.
 *
 * Lists Houston STR properties against HRRB program state (registration,
 * Blue Star training, citation trigger) and drives the register + training
 * actions on the existing houstonHighRisk contract
 * (api/texas/houston/high-risk/*). Data shapes are normalized defensively:
 * buffy's T-106-002/003 deliver the rolling 12-month citation count and
 * auto-trigger alert in parallel; this surface renders whatever is present.
 */
import React, { useCallback, useEffect, useState } from 'react';
import texasApi, { HrrbStatus, StrPortfolioProperty } from '../../services/texasApi';
import { normalizeHrrbSummary, HrrbSummaryCounts } from './HRRBBanner';

const num = (v: any): number => Number(v) || 0;
const pick = (obj: any, keys: string[]): any => {
    if (!obj) return undefined;
    for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return undefined;
};

interface HrrbRow extends StrPortfolioProperty {
    status: HrrbStatus | null;
    statusLoaded: boolean;
    trigger: { triggered: boolean; citationCount: number } | null;
}

const bool = (v: any): boolean => v === true || v === 'true' || v === 1;

export const HRRBRegistrationsPage: React.FC = () => {
    const [threshold, setThreshold] = useState<number>(10);
    const [summary, setSummary] = useState<HrrbSummaryCounts>({ registered: 0, needingTraining: 0, overdueRegistration: 0 });
    const [rows, setRows] = useState<HrrbRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [actionMessage, setActionMessage] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        setActionMessage(null);
        try {
            const [metaRaw, summaryRaw, houston] = await Promise.allSettled([
                texasApi.houstonHighRisk.getMeta(),
                texasApi.houstonHighRisk.getPortfolioSummary(),
                texasApi.strPortfolio.getPortfolio('houston'),
            ]);

            const meta = metaRaw.status === 'fulfilled' ? metaRaw.value : null;
            const metaPayload = meta?.data?.data ?? meta?.data ?? meta;
            setThreshold(num(pick(metaPayload, ['hrrbCitationThreshold', 'citationThreshold'])) || 10);
            setSummary(normalizeHrrbSummary(summaryRaw.status === 'fulfilled' ? summaryRaw.value : null));

            const props: StrPortfolioProperty[] =
                houston.status === 'fulfilled' && Array.isArray(houston.value?.properties)
                    ? houston.value.properties.slice(0, 50)
                    : [];

            const enriched = await Promise.all(
                props.map(async (p): Promise<HrrbRow> => {
                    const base: HrrbRow = {
                        ...p,
                        status: null,
                        statusLoaded: false,
                        trigger: null,
                    };
                    const [statusRes, triggerRes] = await Promise.allSettled([
                        texasApi.houstonHighRisk.getHrrbStatus(p.propertyId),
                        texasApi.houstonHighRisk.getTrigger(p.propertyId),
                    ]);
                    return {
                        ...base,
                        status: statusRes.status === 'fulfilled' ? statusRes.value : null,
                        statusLoaded: true,
                        trigger:
                            triggerRes.status === 'fulfilled'
                                ? {
                                      triggered: bool(pick(triggerRes.value, ['triggered', 'isTriggered'])),
                                      citationCount: num(pick(triggerRes.value, ['citationCount', 'count'])),
                                  }
                                : null,
                    };
                }),
            );
            setRows(enriched);
        } catch (e: any) {
            setError(e?.message || 'Unable to load HRRB data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const refreshRow = async (propertyId: string) => {
        const [statusRes, triggerRes] = await Promise.allSettled([
            texasApi.houstonHighRisk.getHrrbStatus(propertyId),
            texasApi.houstonHighRisk.getTrigger(propertyId),
        ]);
        setRows((prev) =>
            prev.map((r) =>
                r.propertyId === propertyId
                    ? {
                          ...r,
                          status: statusRes.status === 'fulfilled' ? statusRes.value : r.status,
                          statusLoaded: true,
                          trigger:
                              triggerRes.status === 'fulfilled'
                                  ? {
                                        triggered: bool(pick(triggerRes.value, ['triggered', 'isTriggered'])),
                                        citationCount: num(pick(triggerRes.value, ['citationCount', 'count'])),
                                    }
                                  : r.trigger,
                      }
                    : r,
            ),
        );
    };

    const register = async (propertyId: string) => {
        setBusy(propertyId);
        setActionMessage(null);
        try {
            await texasApi.houstonHighRisk.registerForHrrb(propertyId);
            setActionMessage(`Registered ${propertyId} for HRRB.`);
            await refreshRow(propertyId);
        } catch (e: any) {
            setError(e?.message || 'Registration failed');
        } finally {
            setBusy(null);
        }
    };

    const completeTraining = async (propertyId: string) => {
        setBusy(propertyId);
        setActionMessage(null);
        try {
            await texasApi.houstonHighRisk.completeHrrbTraining({ propertyId, blueStarStatus: 'completed' });
            setActionMessage(`Blue Star training marked complete for ${propertyId}.`);
            await refreshRow(propertyId);
        } catch (e: any) {
            setError(e?.message || 'Could not update training status');
        } finally {
            setBusy(null);
        }
    };

    const trainingDone = (row: HrrbRow): boolean => {
        if (!row.status) return false;
        const ts = pick(row.status, ['trainingCompleted', 'blueStarStatus']);
        if (typeof ts === 'string') return String(ts).toLowerCase() === 'completed';
        return bool(ts);
    };

    return (
        <div className="texas-page-container" data-testid="hrrb-page">
            <h1>Houston HRRB Registrations</h1>
            <p className="page-description" data-testid="hrrb-description">
                Houston High-Risk / Blue Star program — registration, Blue Star training, and citation trigger
                tracking. Re-registration is triggered automatically at 12-month rolling {threshold}+ citations.
            </p>

            {actionMessage && (
                <div className="success" data-testid="hrrb-action-success">{actionMessage}</div>
            )}
            {error && (
                <div className="error" data-testid="hrrb-error">{error}</div>
            )}

            <div className="result-grid">
                <div className="result-card" data-testid="hrrb-summary-registered">
                    <strong>{summary.registered || '—'}</strong>
                    <span>Registered &amp; current</span>
                </div>
                <div className="result-card" data-testid="hrrb-summary-training">
                    <strong>{summary.needingTraining || '—'}</strong>
                    <span>Need Blue Star training</span>
                </div>
                <div className="result-card" data-testid="hrrb-summary-overdue">
                    <strong>{summary.overdueRegistration || '—'}</strong>
                    <span>At citation re-registration trigger</span>
                </div>
            </div>

            {loading && <p className="no-results" data-testid="hrrb-loading">Loading HRRB status…</p>}

            {!loading && rows.length === 0 && (
                <div className="no-results" data-testid="hrrb-empty">
                    No Houston STR properties found. HRRB tracking is limited to Houston registrations.
                </div>
            )}

            {!loading && rows.length > 0 && (
                <div className="result-section">
                    <h2>Properties</h2>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500 border-b">
                                <th className="py-2 pr-4">Property</th>
                                <th className="py-2 pr-4">Registration</th>
                                <th className="py-2 pr-4">Blue Star</th>
                                <th className="py-2 pr-4">Citations</th>
                                <th className="py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const reg = registeredOf(row);
                                const training = trainingDone(row);
                                const trig = row.trigger;
                                return (
                                    <tr key={row.propertyId} className="border-b last:border-0" data-testid="hrrb-property-row">
                                        <td className="py-3 pr-4 font-medium text-gray-800" data-testid="hrrb-property-id">
                                            {row.propertyId}
                                        </td>
                                        <td className="py-3 pr-4">
                                            {!row.statusLoaded ? (
                                                <span className="text-gray-400">…</span>
                                            ) : reg ? (
                                                <span className="success">Registered</span>
                                            ) : (
                                                <span className="red-warning">Not registered</span>
                                            )}
                                        </td>
                                        <td className="py-3 pr-4">
                                            {!row.statusLoaded ? (
                                                <span className="text-gray-400">…</span>
                                            ) : training ? (
                                                <span className="success">Complete</span>
                                            ) : (
                                                <span className="red-warning">Pending</span>
                                            )}
                                        </td>
                                        <td className="py-3 pr-4">
                                            {trig ? (
                                                <span
                                                    className={trig.triggered ? 'red-warning' : 'text-gray-600'}
                                                    data-testid={`hrrb-citation-${row.propertyId}`}
                                                >
                                                    {trig.citationCount}/{threshold}
                                                    {trig.triggered ? ' — trigger crossed' : ''}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-2">
                                                {!reg && (
                                                    <button
                                                        onClick={() => register(row.propertyId)}
                                                        disabled={busy === row.propertyId}
                                                        className="lease-input-btn"
                                                        data-testid={`hrrb-register-${row.propertyId}`}
                                                    >
                                                        Register
                                                    </button>
                                                )}
                                                {!training && (
                                                    <button
                                                        onClick={() => completeTraining(row.propertyId)}
                                                        disabled={busy === row.propertyId}
                                                        className="lease-input-btn"
                                                        data-testid={`hrrb-training-${row.propertyId}`}
                                                    >
                                                        Mark training done
                                                    </button>
                                                )}
                                                {reg && training && (
                                                    <span className="text-gray-400 text-xs">All set</span>
                                                )}
                                                {busy === row.propertyId && (
                                                    <span className="text-gray-400 text-xs">Saving…</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

function registeredOf(row: HrrbRow): boolean {
    if (row.status === null) return false;
    return bool(pick(row.status, ['registered', 'isRegistered']));
}

export default HRRBRegistrationsPage;